import React, { useState, useEffect } from "react";
import { Link, useLocation, Outlet } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import {
  FiHome,
  FiShoppingBag,
  FiDollarSign,
  FiList,
  FiUser,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiCreditCard,
  FiUsers,
  FiTrendingUp,
  FiSmartphone,
  FiGift,
} from "react-icons/fi";
import { BsWallet2, BsPhone, BsGraphUp } from "react-icons/bs";
import { logout, getProfile } from "../../store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

export const SidebarLayout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);

  // Close mobile sidebar when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Fetch user profile/balance on each page navigation
  useEffect(() => {
    dispatch(getProfile());
  }, [location.pathname, dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    toast.success("Logged out successfully");
    navigate("/");
  };

  const userLinks = [
    {
      path: "/dashboard",
      icon: FiHome,
      label: "Dashboard",
      color: "from-blue-500 to-blue-600",
      description: "Overview & stats",
    },
    {
      path: "/accounts",
      icon: FiShoppingBag,
      label: "Buy Accounts",
      color: "from-purple-500 to-purple-600",
      description: "Browse & purchase",
    },
    {
      path: "/tools-and-updates",
      icon: FiShoppingBag,
      label: "Buy Tools & Updates",
      color: "from-purple-500 to-purple-600",
      description: "Browse & purchase",
    },
    {
      path: "/social-boost",
      icon: FiTrendingUp,
      label: "Social Boost",
      color: "from-green-500 to-green-600",
      description: "Grow your presence",
      badge: "New",
    },
    {
      path: "/virtual-numbers",
      icon: BsPhone,
      label: "Purchase Numbers",
      color: "from-cyan-500 to-cyan-600",
      description: "Browse virtual numbers",
    },
    {
      path: "/my-numbers",
      icon: FiSmartphone,
      label: "My Numbers",
      color: "from-cyan-500 to-cyan-600",
      description: "View purchased numbers",
    },
    {
      path: "/deposit",
      icon: FiDollarSign,
      label: "Deposit Funds",
      color: "from-emerald-500 to-emerald-600",
      description: "Add to wallet",
    },
    {
      path: "/transactions",
      icon: FiList,
      label: "Transactions",
      color: "from-orange-500 to-orange-600",
      description: "Payment history",
    },
    {
      path: "/orders",
      icon: FiCreditCard,
      label: "My Orders",
      color: "from-pink-500 to-pink-600",
      description: "Purchase history",
    },
    {
      path: "/profile",
      icon: FiUser,
      label: "Profile",
      color: "from-indigo-500 to-indigo-600",
      description: "Account settings",
    },
  ];

  const adminLinks = [
    {
      path: "/admin",
      icon: FiHome,
      label: "Admin Dashboard",
      color: "from-red-500 to-red-600",
      description: "Platform overview",
    },
    {
      path: "/admin/users",
      icon: FiUsers,
      label: "Manage Users",
      color: "from-red-500 to-red-600",
      description: "User management",
    },
    {
      path: "/admin/accounts",
      icon: FiShoppingBag,
      label: "Sell Accounts",
      color: "from-red-500 to-red-600",
      description: "Account listings",
    },
    {
      path: "/admin/virtual-numbers",
      icon: BsPhone,
      label: "Manage Virtual Numbers Pricing",
      color: "from-red-500 to-red-600",
      description: "Manage pricing & sync",
    },
    {
      path: "/admin/virtual-number-purchases",
      icon: FiSmartphone,
      label: "Manage Number Purchases",
      color: "from-red-500 to-red-600",
      description: "See all number purchases",
    },
    {
      path: "/admin/tools-and-updates",
      icon: FiShoppingBag,
      label: "Tools & Updates",
      color: "from-red-500 to-red-600",
      description: "Account listings",
    },
    {
      path: "/admin/payment-config",
      icon: FiCreditCard,
      label: "Payment Methods",
      color: "from-red-500 to-red-600",
      description: "Configure payment gateways",
    },
    {
      path: "/admin/transactions",
      icon: FiList,
      label: "All Transactions",
      color: "from-red-500 to-red-600",
      description: "Payment monitoring",
    },
    {
      path: "/admin/analytics",
      icon: FiTrendingUp,
      label: "Analytics",
      color: "from-red-500 to-red-600",
      description: "Platform analytics",
    },
    {
      path: "/admin/settings",
      icon: FiSettings,
      label: "Settings",
      color: "from-red-500 to-red-600",
      description: "Platform config",
    },
  ];

  const links =
    user?.role === "admin" ? [...adminLinks, ...userLinks] : userLinks;
  const currentLink = links.find((link) => link.path === location.pathname);
  const pageTitle = currentLink?.label || "Dashboard";

  return (
    <div className="flex min-h-screen bg-linear-to-br from-luxury-50 via-white to-luxury-100">
      {/* Sidebar - Desktop */}
      <motion.aside
        initial={false}
        animate={{ width: isCollapsed ? "80px" : "280px" }}
        transition={{ duration: 0.1 }}
        className="hidden lg:block fixed left-0 top-0 h-full glass border-r border-luxury-200 z-40 overflow-y-auto overflow-x-hidden"
      >
        <div className="relative h-full flex flex-col">
          {/* Logo */}
          <div
            onClick={() => navigate("/")}
            className={`p-6 cursor-pointer flex items-center ${isCollapsed ? "justify-center" : "space-x-3"}`}
          >
            <div
              className={`${isCollapsed ? "w-[50px]" : "w-[70px]"}  flex items-center justify-center shrink-0`}
            >
              <img src="/nitropng.png" alt="Logo" className="w-full h-full " />
            </div>
            {!isCollapsed && (
              <motion.span
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xl font-display font-bold"
              >
                <span className="gradient-text">Nitro</span>
                <span className="text-luxury-900">Logs</span>
              </motion.span>
            )}
          </div>

          {/* Toggle Button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="absolute top-15 right-3 w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center text-white shadow-luxury hover:bg-primary-700 transition-colors"
          >
            {isCollapsed ? (
              <FiChevronRight className="w-4 h-4" />
            ) : (
              <FiChevronLeft className="w-4 h-4" />
            )}
          </button>

          {/* User Info */}
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mx-4 p-4 glass rounded-xl mb-4"
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-linear-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                  <span className="text-white font-semibold">
                    {user?.username?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-luxury-900 truncate">
                    {user?.username}
                  </p>
                  <p className="text-xs text-luxury-500 truncate">
                    {user?.email}
                  </p>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-luxury-200">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-luxury-600">Balance</span>
                  <span className="font-bold gradient-text">
                    ₦{user?.balance?.toLocaleString() || 0}
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {/* Navigation Links */}
          <nav className="flex-1 overflow-y-auto py-4">
            {links.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative flex items-center ${isCollapsed ? "justify-center" : "space-x-3"} mx-2 px-4 py-3 rounded-xl transition-all duration-300 group ${
                    isActive
                      ? `bg-linear-to-r ${link.color} text-white shadow-luxury`
                      : "text-luxury-600 hover:bg-luxury-100"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${isActive ? "text-white" : "text-luxury-500"}`}
                  />
                  {!isCollapsed && (
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm font-medium">{link.label}</span>
                      {link.badge && (
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            link.badge === "New"
                              ? "bg-green-100 text-green-700"
                              : "bg-orange-100 text-orange-700"
                          }`}
                        >
                          {link.badge}
                        </span>
                      )}
                    </div>
                  )}
                  {isCollapsed && (
                    <>
                      <div className="absolute left-full ml-2 px-2 py-1 bg-luxury-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
                        {link.label}
                      </div>
                      {link.badge && (
                        <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-500 rounded-full" />
                      )}
                    </>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="p-4 border-t border-luxury-200">
            <button
              onClick={handleLogout}
              className={`flex items-center ${isCollapsed ? "justify-center" : "space-x-3"} w-full px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors`}
            >
              <FiLogOut className="w-5 h-5" />
              {!isCollapsed && (
                <span className="text-sm font-medium">Logout</span>
              )}
            </button>
          </div>
        </div>
      </motion.aside>

      {/* Sidebar - Mobile */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "spring", damping: 20, stiffness: 100 }}
              className="lg:hidden fixed left-0 top-0 w-68 h-screen z-70 overflow-y-auto overflow-x-hidden glass border-r border-luxury-200 rounded-r-2xl"
            >
              <div className="h-full flex flex-col">
                {/* Logo */}
                <div className="p-6 flex items-center space-x-3">
                  <div className="w-10 flex items-center justify-center">
                    <img
                      src="/nitropng.png"
                      alt="Logo"
                      className="w-full h-full text-white"
                    />
                  </div>
                  <span className="text-xl font-display font-bold">
                    <span className="gradient-text">Nitro</span>
                    <span className="text-luxury-900">Logs</span>
                  </span>
                </div>

                {/* User Info */}
                <div className="mx-4 p-4 glass rounded-xl mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-linear-to-r from-primary-500 to-secondary-500 rounded-xl flex items-center justify-center">
                      <span className="text-white font-semibold">
                        {user?.username?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-luxury-900 truncate">
                        {user?.username}
                      </p>
                      <p className="text-xs text-luxury-800 truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-luxury-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-luxury-900">Balance</span>
                      <span className="font-bold gradient-text">
                        ₦{user?.balance?.toLocaleString() || 0}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4">
                  {links.map((link) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.path;
                    return (
                      <Link
                        key={link.path}
                        to={link.path}
                        className={`flex items-center justify-between mx-2 px-4 py-3 rounded-xl transition-all duration-300 ${
                          isActive
                            ? `bg-linear-to-r ${link.color} text-white shadow-luxury`
                            : "text-luxury-900 hover:bg-luxury-100"
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Icon
                            className={`w-5 h-5 ${isActive ? "text-white" : "text-luxury-500"}`}
                          />
                          <span className="text-sm font-medium">
                            {link.label}
                          </span>
                        </div>
                        {link.badge && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              link.badge === "New"
                                ? "bg-green-100 text-green-700"
                                : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {link.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </nav>

                {/* Logout */}
                <div className="p-4 border-t border-luxury-200">
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-3 w-full px-4 py-3 rounded-xl text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <FiLogOut className="w-5 h-5" />
                    <span className="text-sm font-medium">Logout</span>
                  </button>
                </div>
              </div>
            </motion.aside>

            {/* Overlay for mobile */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-60 lg:hidden"
              onClick={() => setIsMobileOpen(false)}
            />
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-200 w-full overflow-x-hidden ${
          isCollapsed ? "lg:ml-20" : "lg:ml-72"
        } pt-16`}
      >
        <div className="min-h-screen max-w-full">
          <div
            className={`fixed top-0 left-0 right-0 z-45 flex items-center justify-between h-16 px-4 border-b border-gray-200 bg-gray-100 ${
              isCollapsed ? "lg:left-20" : "lg:left-72"
            }`}
          >
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="lg:hidden p-2 rounded-lg bg-white border border-gray-200 text-luxury-900 shadow-sm"
              >
                {isMobileOpen ? (
                  <FiX className="w-5 h-5" />
                ) : (
                  <FiMenu className="w-5 h-5" />
                )}
              </button>
              <h1 className="text-lg font-semibold text-luxury-900">
                {pageTitle}
              </h1>
            </div>
            <button
              onClick={() => navigate("/profile")}
              className="p-2 rounded-lg bg-white border border-gray-200 text-luxury-900 shadow-sm"
            >
              <FiUser className="w-5 h-5" />
            </button>
          </div>
          <div className="pt-1">
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};
